import type { RoomId } from '../ids';
import type { Load, RcuConfig, WiredLoad } from '../model';
import { isWired } from '../model';
import { findRoom } from './lookup';

export const wiredLoads = (config: RcuConfig): readonly WiredLoad[] => config.loads.filter(isWired);

/** Created but not yet wired to an output, so not controllable. */
export const unwiredLoads = (config: RcuConfig): readonly Load[] =>
  config.loads.filter((load) => !isWired(load));

export const loadsInRoom = (config: RcuConfig, roomId: RoomId): readonly WiredLoad[] =>
  wiredLoads(config).filter((load) => load.roomId === roomId);

/**
 * Wired loads that belong to no existing room — never assigned, or their room
 * was deleted. These stay controllable under the Unassigned tab.
 */
export const unassignedWiredLoads = (config: RcuConfig): readonly WiredLoad[] =>
  wiredLoads(config).filter(
    (load) => load.roomId === null || findRoom(config, load.roomId) === undefined,
  );

export interface LoadGroup {
  /** null denotes the Unassigned group. */
  readonly roomId: RoomId | null;
  /** Room name, or empty for the Unassigned group, which the UI labels itself. */
  readonly name: string;
  readonly loads: readonly WiredLoad[];
}

/**
 * Groups shown on the controls page: every room that holds wired loads, in
 * configuration order, then Unassigned when anything is left over.
 */
export const controlGroups = (config: RcuConfig): readonly LoadGroup[] => {
  const roomGroups: LoadGroup[] = config.rooms
    .map((room) => ({ roomId: room.id, name: room.name, loads: loadsInRoom(config, room.id) }))
    .filter((group) => group.loads.length > 0);
  const unassigned = unassignedWiredLoads(config);
  return unassigned.length > 0
    ? [...roomGroups, { roomId: null, name: '', loads: unassigned }]
    : roomGroups;
};
